import { useEffect, useState, type ChangeEvent } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Pencil, Plus, Search, Star } from 'lucide-react'

import { api } from '@/api/client'
import { useAuth, type Company, type User } from '@/stores/auth'
import { useToast } from '@/stores/toast'
import { Modal } from '@/components/Modal'
import { cn } from '@/lib/utils'
import { ADMIN_TABS, type AdminTab, adminUrl, adminWindowName, focusWindow, mainWindowName } from '@/lib/windows'
import { DailySalesTab } from './DailySalesTab'
import { PurchaseMapManage, type PurchaseMapConfig } from './PurchaseMapManage'
import { PartnerAliasManage, type AliasConfig } from './PartnerAliasManage'
import { PurchaseRegisterAllTab } from './PurchaseRegisterAllTab'
import { PurchaseNameOrderTab } from './PurchaseNameOrderTab'
import { LabelStorageRuleTab } from './LabelStorageRuleTab'
import { MzCenterLabelTab } from './MzCenterLabelTab'
import { SupplierManageTab } from './SupplierManageTab'
import { SupplierOrdersTab } from './SupplierOrdersTab'
import { PartnerStatusTab } from './PartnerStatusTab'
import { AnalysisTab } from './AnalysisTab'

const PURCHASE_MAP_CFG: PurchaseMapConfig = {
  apiBase: '/api/purchase/item-maps', queryKey: 'purchase-item-maps',
  showLabelUnit: false, title: '구매장용', allowCommon: true, showSupplier: true,
}
const LABEL_MAP_CFG: PurchaseMapConfig = {
  apiBase: '/api/purchase/label-maps', queryKey: 'label-item-maps',
  showLabelUnit: true, title: '라벨용', allowCommon: true,
}
const PURCHASE_ALIAS_CFG: AliasConfig = {
  apiBase: '/api/purchase/partner-aliases', queryKey: 'purchase-partner-aliases',
  aliasLabel: '구매장용 거래처',
}
const LABEL_ALIAS_CFG: AliasConfig = {
  apiBase: '/api/purchase/label-partner-aliases', queryKey: 'label-partner-aliases',
  aliasLabel: '라벨용 거래처',
}

interface CompanyForm {
  name: string
  biz_type: '법인' | '개인'
  biz_no: string
  rep_name: string
  biz_kind: string
  biz_item: string
  address: string
  tel: string
  fax: string
  invoice_style: 'common' | 'greenseoul' | 'marufarm' | 'dawon'
  label_group: string
  sales_merge_into: number | null
  seal: string
  is_default: boolean
}

const blankForm: CompanyForm = {
  name: '', biz_type: '법인', biz_no: '', rep_name: '',
  biz_kind: '', biz_item: '', address: '', tel: '', fax: '',
  invoice_style: 'common', label_group: '', sales_merge_into: null, seal: '', is_default: false,
}

const INVOICE_STYLE_LABELS: Record<CompanyForm['invoice_style'], string> = {
  common: '공통(그린푸드)', greenseoul: '그린서울', marufarm: '마루팜', dawon: '다원농산',
}

// 회사관리 화면 탭 — 탭마다 자기 창(?tab=). 창 하나 = 탭 하나로 고정해, 다른 탭을 누르면
// 그 탭의 창으로 이동(없으면 새 창). 창 안에서 탭을 바꾸면 창 이름과 내용이 어긋나 같은 창을
// 다시 찾는 문제가 있어서(2026-11-04 테스트) 전 탭을 창 단위로 바꿨다.
type PageTab = AdminTab
const isPageTab = (t: string): t is PageTab => (ADMIN_TABS as readonly string[]).includes(t)
const windowNameFor = adminWindowName
const urlFor = adminUrl
function initialTab(): PageTab {
  const t = new URLSearchParams(window.location.search).get('tab') ?? ''
  return isPageTab(t) ? t : '회사'
}

export function CompanySelectPage() {
  const nav = useNavigate()
  const qc = useQueryClient()
  const { logout } = useAuth()
  const isMaster = useAuth((s) => s.user?.is_master)
  const canAnalyze = useAuth((s) => !!(s.user?.is_master || s.user?.can_analyze))
  // 권한(분석 화면 등)은 로그인 때만 받아오므로, 관리자가 바꾼 권한이 재로그인 없이 반영되게 갱신.
  useEffect(() => {
    api<User>('/api/auth/me').then((u) => useAuth.getState().setUser(u)).catch(() => {})
  }, [])
  const push = useToast((s) => s.push)

  const [search, setSearch] = useState('')
  const [open, setOpen] = useState(false)
  const [tab, setTab] = useState<PageTab>(initialTab)
  useEffect(() => { window.name = windowNameFor(tab) }, [tab])
  function openTab(t: PageTab) {
    if (t === tab) return
    // 탭별 고정 창 이름 — 이미 열려 있으면 그 창으로 이동(작업 보존), 없으면 새 창.
    if (!focusWindow(windowNameFor(t), urlFor(t))) {   // 팝업 차단 → 이 창에서 전환
      setTab(t); window.history.replaceState(null, '', urlFor(t))
    }
  }
  const [purSub, setPurSub] = useState<'품목' | '거래처' | '정렬' | '협력' | '보기' | '발주' | '현황'>('품목')
  const [labSub, setLabSub] = useState<'품목' | '거래처' | '보관' | '보기' | '엠즈'>('품목')
  const [editId, setEditId] = useState<number | null>(null)
  const [form, setForm] = useState<CompanyForm>(blankForm)

  const { data: companies = [] } = useQuery({
    queryKey: ['companies', search],
    queryFn: () => api<Company[]>(`/api/companies?q=${encodeURIComponent(search)}`),
  })

  const save = useMutation({
    mutationFn: (body: CompanyForm) =>
      editId == null
        ? api<Company>('/api/companies', { method: 'POST', body })
        : api<Company>(`/api/companies/${editId}`, { method: 'PATCH', body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['companies'] })
      push(editId == null ? '회사 추가됨' : '회사 정보 저장됨', 'success')
      setOpen(false); setForm(blankForm); setEditId(null)
    },
    onError: (e) => push(`저장 실패: ${e.message}`, 'error'),
  })

  function openAdd() {
    setEditId(null); setForm(blankForm); setOpen(true)
  }
  function openEdit(c: Company) {
    setEditId(c.id)
    setForm({
      name: c.name, biz_type: c.biz_type, biz_no: c.biz_no || '', rep_name: c.rep_name || '',
      biz_kind: c.biz_kind || '', biz_item: c.biz_item || '', address: c.address || '',
      tel: c.tel || '', fax: c.fax || '',
      invoice_style: c.invoice_style || 'common', label_group: c.label_group || '',
      sales_merge_into: c.sales_merge_into ?? null,
      seal: c.seal || '', is_default: c.is_default,
    })
    setOpen(true)
  }

  function onSealFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    e.target.value = ''  // 같은 파일 재선택 허용
    if (!file) return
    if (file.size > 2 * 1024 * 1024) { push('도장 이미지는 2MB 이하로 올려주세요', 'error'); return }
    const reader = new FileReader()
    reader.onload = () => setForm((f) => ({ ...f, seal: String(reader.result || '') }))
    reader.readAsDataURL(file)
  }

  const setDefault = useMutation({
    mutationFn: async (c: Company) => {
      for (const x of companies.filter((p) => p.is_default && p.id !== c.id)) {
        await api(`/api/companies/${x.id}`, { method: 'PATCH', body: { is_default: false } })
      }
      return api<Company>(`/api/companies/${c.id}`, {
        method: 'PATCH', body: { is_default: !c.is_default },
      })
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['companies'] }),
  })

  function selectCompany(c: Company) {
    // 회사별 /main 창으로(열려 있으면 그 창). 이 '회사 선택' 창은 그대로 둔다 — 창을 /main 으로 바꾸면
    // 다음 [회사] 클릭 때 새 회사 선택 창이 생겨 둘이 되던 문제(2026-11-04). 팝업 차단이면 이 창에서 이동.
    if (!focusWindow(mainWindowName(c.id, '/main'), `/main?company=${c.id}`)) {
      nav({ to: '/main', search: { company: c.id } })
    }
  }

  function doLogout() {
    logout()
    nav({ to: '/login' })
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-md">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">회사관리</h1>
            <p className="text-sm text-blue-100 mt-0.5">사용할 회사를 선택하거나 추가하세요</p>
          </div>
          <div className="flex items-center gap-2">
            {isMaster && (
              <button onClick={() => nav({ to: '/users' })}
                      className="text-xs bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-lg transition">
                사용자 관리
              </button>
            )}
            <button onClick={doLogout}
                    className="text-xs bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-lg transition">
              로그아웃
            </button>
          </div>
        </div>
      </header>

      {/* 매출관리 탭: 로그인 사용자 전체 노출(전사 일일매출). */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 flex gap-1">
          {(['회사', '일일매출', '구매장', '라벨', ...(canAnalyze ? ['분석'] as const : [])] as const).map((t) => (
            <button key={t} onClick={() => openTab(t)}
                    title={t !== tab ? '새 창으로 열기(열려 있으면 그 창으로)' : undefined}
                    className={cn('px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition',
                      tab === t ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700')}>
              {t}
            </button>
          ))}
        </div>
      </div>

      {tab === '분석' && canAnalyze && (
        <div className="max-w-6xl mx-auto px-6 py-6"><AnalysisTab /></div>
      )}
      {tab === '일일매출' && (
        <div className="max-w-6xl mx-auto px-6 py-6"><DailySalesTab /></div>
      )}
      {tab === '라벨' && (
        <div>
          <div className="max-w-6xl mx-auto px-6 pt-4">
            <div className="inline-flex rounded-lg border border-slate-300 overflow-hidden text-sm">
              {([['품목', '품목 매핑'], ['거래처', '거래처 별칭'], ['보관', '보관방법'], ['보기', '라벨 보기'], ['엠즈', '엠즈 센터']] as const).map(([k, lbl]) => (
                <button key={k} onClick={() => setLabSub(k)}
                        className={cn('px-4 py-1.5 font-semibold',
                          labSub === k ? 'bg-emerald-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50')}>
                  {lbl}
                </button>
              ))}
            </div>
          </div>
          {labSub === '품목' && <PurchaseMapManage config={LABEL_MAP_CFG} />}
          {labSub === '거래처' && <PartnerAliasManage config={LABEL_ALIAS_CFG} />}
          {labSub === '보관' && <LabelStorageRuleTab />}
          {labSub === '엠즈' && <MzCenterLabelTab />}
          {labSub === '보기' && <PurchaseRegisterAllTab source="label" />}
        </div>
      )}
      {tab === '구매장' && (
        <div>
          <div className="max-w-6xl mx-auto px-6 pt-4">
            <div className="inline-flex rounded-lg border border-slate-300 overflow-hidden text-sm">
              {([['품목', '품목 매핑'], ['거래처', '거래처 별칭'], ['정렬', '품명 정렬'], ['협력', '협력업체'], ['보기', '구매장 보기'], ['발주', '협력업체 발주서'], ['현황', '거래처 현황']] as const).map(([k, lbl]) => (
                <button key={k} onClick={() => setPurSub(k)}
                        className={cn('px-4 py-1.5 font-semibold',
                          purSub === k ? 'bg-emerald-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50')}>
                  {lbl}
                </button>
              ))}
            </div>
          </div>
          {purSub === '품목' && <PurchaseMapManage config={PURCHASE_MAP_CFG} />}
          {purSub === '거래처' && <PartnerAliasManage config={PURCHASE_ALIAS_CFG} />}
          {purSub === '정렬' && <PurchaseNameOrderTab />}
          {purSub === '협력' && <SupplierManageTab />}
          {purSub === '발주' && <SupplierOrdersTab />}
          {purSub === '보기' && <PurchaseRegisterAllTab source="purchase" />}
          {purSub === '현황' && <PartnerStatusTab />}
        </div>
      )}

      <div className="max-w-3xl mx-auto px-6 py-6" hidden={tab !== '회사'}>
        <div className="flex items-center gap-3 mb-5">
          <div className="relative flex-1">
            <input
              type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="회사이름, 사업자번호, 대표자명으로 검색하세요"
              className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-slate-300
                         focus:border-blue-500 focus:ring-2 focus:ring-blue-100
                         text-sm outline-none bg-white"
            />
            <Search className="absolute left-3.5 top-3.5 w-4 h-4 text-slate-400" />
          </div>
          <button onClick={openAdd}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold
                             rounded-lg px-4 py-2.5 inline-flex items-center gap-1.5">
            <Plus className="w-4 h-4" /> 회사 추가
          </button>
        </div>

        <div className="space-y-2.5">
          {companies.map((c) => (
            <button
              key={c.id}
              onClick={() => selectCompany(c)}
              className={cn(
                'w-full bg-white border-2 rounded-xl p-4 text-left transition-all',
                'hover:border-blue-400 hover:shadow-md flex items-start gap-4',
                c.is_default ? 'border-blue-500' : 'border-slate-200'
              )}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <span className="font-semibold text-slate-900 text-base truncate">{c.name}</span>
                  {c.is_default && (
                    <span className="text-[10px] font-bold text-blue-700 bg-blue-100
                                     px-2 py-0.5 rounded-full">대표</span>
                  )}
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-600">
                  <span className={cn(
                    'px-2 py-0.5 rounded font-semibold',
                    c.biz_type === '법인' ? 'bg-emerald-100 text-emerald-700' : 'bg-purple-100 text-purple-700'
                  )}>
                    {c.biz_type}
                  </span>
                  <span className="tabular-nums">{c.biz_no || '—'}</span>
                  {c.rep_name && <><span className="text-slate-400">|</span>
                    <span className="text-slate-700">{c.rep_name}</span></>}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div
                  role="button" tabIndex={0}
                  onClick={(e) => { e.stopPropagation(); openEdit(c) }}
                  className="text-slate-300 hover:text-blue-500 transition"
                  title="회사 정보 수정"
                >
                  <Pencil className="w-4 h-4" />
                </div>
                <div
                  role="button" tabIndex={0}
                  onClick={(e) => { e.stopPropagation(); setDefault.mutate(c) }}
                  className="text-slate-300 hover:text-amber-500 transition"
                  title={c.is_default ? '대표 회사' : '대표로 설정'}
                >
                  <Star className={cn('w-5 h-5', c.is_default && 'fill-amber-400 text-amber-400')} />
                </div>
              </div>
            </button>
          ))}

          {!companies.length && (
            <div className="text-center text-slate-400 py-12 text-sm">
              등록된 회사가 없습니다. "회사 추가"로 시작하세요.
            </div>
          )}
        </div>
      </div>

      <Modal open={open} onClose={() => { setOpen(false); setEditId(null) }}
             title={editId == null ? '회사 추가' : '회사 수정'}>
        <form onSubmit={(e) => { e.preventDefault(); save.mutate(form) }} className="space-y-3">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">회사명 *</label>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                   required className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"/>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">구분</label>
              <select value={form.biz_type} onChange={(e) => setForm({ ...form, biz_type: e.target.value as '법인' | '개인' })}
                      className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm bg-white">
                <option value="법인">법인</option>
                <option value="개인">개인</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">사업자번호</label>
              <input value={form.biz_no} onChange={(e) => setForm({ ...form, biz_no: e.target.value })}
                     placeholder="000-00-00000"
                     className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm tabular-nums outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"/>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">대표자명</label>
              <input value={form.rep_name} onChange={(e) => setForm({ ...form, rep_name: e.target.value })}
                     className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"/>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">거래명세서 양식</label>
              <select value={form.invoice_style} onChange={(e) => setForm({ ...form, invoice_style: e.target.value as CompanyForm['invoice_style'] })}
                      className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm bg-white">
                {(Object.keys(INVOICE_STYLE_LABELS) as CompanyForm['invoice_style'][]).map((k) => (
                  <option key={k} value={k}>{INVOICE_STYLE_LABELS[k]}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">
              라벨 출력 묶음 <span className="font-normal text-slate-400">— 같은 값인 회사끼리 라벨을 한 번에 출력(비우면 묶음 없음)</span>
            </label>
            <input value={form.label_group} onChange={(e) => setForm({ ...form, label_group: e.target.value })}
                   list="company-label-groups" placeholder="예: 그린푸드·그린서울·허브팜"
                   className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"/>
            <datalist id="company-label-groups">
              {[...new Set(companies.map((c) => c.label_group).filter(Boolean))].map((g) => <option key={g} value={g}/>)}
            </datalist>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">
              일일매출 합산 회사 <span className="font-normal text-slate-400">— 지정하면 일일매출에서 이 회사 줄이 그 회사 블록·합계에 포함(출고·채권은 별도)</span>
            </label>
            <select value={form.sales_merge_into ?? ''}
                    onChange={(e) => setForm({ ...form, sales_merge_into: e.target.value ? Number(e.target.value) : null })}
                    className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm bg-white">
              <option value="">없음 (따로 표시)</option>
              {companies.filter((c) => c.id !== editId).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">업태</label>
              <input value={form.biz_kind} onChange={(e) => setForm({ ...form, biz_kind: e.target.value })}
                     className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"/>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">종목</label>
              <input value={form.biz_item} onChange={(e) => setForm({ ...form, biz_item: e.target.value })}
                     className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"/>
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">사업장주소</label>
            <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
                   className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"/>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">전화번호</label>
              <input value={form.tel} onChange={(e) => setForm({ ...form, tel: e.target.value })}
                     className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm tabular-nums outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"/>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1">팩스번호</label>
              <input value={form.fax} onChange={(e) => setForm({ ...form, fax: e.target.value })}
                     className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm tabular-nums outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"/>
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">도장(인감) 이미지</label>
            <div className="flex items-center gap-3">
              {form.seal
                ? <img src={form.seal} alt="도장" className="w-16 h-16 object-contain border border-slate-200 rounded bg-white" />
                : <div className="w-16 h-16 grid place-items-center border border-dashed border-slate-300 rounded text-[10px] text-slate-400">없음</div>}
              <div className="flex flex-col gap-1.5">
                <label className="text-xs bg-slate-100 hover:bg-slate-200 rounded px-3 py-1.5 cursor-pointer w-fit">
                  이미지 선택
                  <input type="file" accept="image/*" onChange={onSealFile} className="hidden" />
                </label>
                {form.seal && (
                  <button type="button" onClick={() => setForm({ ...form, seal: '' })}
                          className="text-xs text-rose-600 hover:text-rose-800 w-fit">도장 제거</button>
                )}
                <span className="text-[10px] text-slate-400">거래명세서 공급자란에 찍힙니다 (2MB 이하)</span>
              </div>
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700 mt-2">
            <input type="checkbox" checked={form.is_default}
                   onChange={(e) => setForm({ ...form, is_default: e.target.checked })}/>
            대표 회사로 설정
          </label>
          <div className="flex justify-end gap-2 pt-3">
            <button type="button" onClick={() => { setOpen(false); setEditId(null) }}
                    className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">취소</button>
            <button type="submit" disabled={!form.name || save.isPending}
                    className="px-4 py-2 text-sm bg-emerald-600 hover:bg-emerald-700 disabled:bg-blue-300 text-white font-semibold rounded-lg">
              {editId == null ? '추가' : '저장'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  )
}
